/**
 * Telemedicine · Phase 1 · P1.UI Phase E
 *
 * Module-level handoff between the Lobby (`app/video-call/lobby.tsx`) and the
 * in-call screen (`app/video-call/[channel].tsx`). Used so the lobby can:
 *
 *   - mint the RTC token once and avoid a second round-trip on Entrar
 *   - hand the user's mic/camera preferences (set via the lobby toggle pills)
 *     forward to the call screen, which re-applies them right after join
 *
 * Why not route params? RTC tokens are JWTs; expo-router serializes params
 * into deep-link URLs that can show up in OS logs and crash reports. Keeping
 * the JWT in process memory only is the safer default.
 *
 * Lifecycle: a single handoff entry is keyed by sessionId. consume() reads
 * AND deletes — the call screen is the only consumer, and a stale entry from
 * a prior cancelled flow must never be picked up by a new attempt. Entries
 * also auto-expire after 5 minutes as a safety net for screens that mount
 * the lobby and never advance.
 */
import type { AgoraCallCredentials } from '@/hooks/useAgoraCall';

export interface VideoCallHandoff {
  /** RTC creds — token is short-lived (Agora default 24h, but we treat as ephemeral). */
  creds: AgoraCallCredentials;
  /** User's mode preference at the moment they tapped Entrar. */
  mode: 'audio' | 'video';
  /** True if the user toggled their mic off in the lobby. */
  initialMicMuted: boolean;
  /** True if the user toggled their camera off in the lobby. */
  initialCamOff: boolean;
  /** ms since epoch when the handoff was set; used for the safety expiry. */
  setAt: number;
}

const TTL_MS = 5 * 60 * 1000;
const store = new Map<string, VideoCallHandoff>();

export function setHandoff(sessionId: string, handoff: Omit<VideoCallHandoff, 'setAt'>): void {
  if (!sessionId) return;
  store.set(sessionId, { ...handoff, setAt: Date.now() });
}

export function consumeHandoff(sessionId: string): VideoCallHandoff | null {
  if (!sessionId) return null;
  const entry = store.get(sessionId);
  if (!entry) return null;
  store.delete(sessionId);
  if (Date.now() - entry.setAt > TTL_MS) {
    return null;
  }
  return entry;
}

export function clearHandoff(sessionId: string): void {
  if (!sessionId) return;
  store.delete(sessionId);
}
